import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  useCandidateWithApplicationsQuery,
  useJobWithStagesQuery,
  PipelineStageType,
  useScorecardsQuery,
  ScorecardsOrderBy,
} from "../../graphql/generated/graphql";
import CandidateSkeleton from "../../components/Skeletons/CandidateSkeleton";
import EmptyPage from "../../components/Empty/EmptyPage";
import Questionnaires from "../../components/Candidate/Questionnaires";
import CandidateActions from "../../components/Candidate/CandidateActions";
import person from "../../image/placeholder.png";
import NavBar from "../../components/NavBar/NavBar";
import EvaluationListItems from "../../components/Evaluations/EvaluationsListItems";
import EvaluateModal from "../../components/Evaluations/EvaluateModal";
import EvaluationDetailsModal from "../../components/Evaluations/EvaluationDetailsModal";

export default function Candidate() {
  const { id } = useParams<{ id: string }>();
  const candidateId = parseInt(id || "0", 10);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState<
    number | null
  >(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedScorecardTitle, setSelectedScorecardTitle] =
    useState<string>("");

  const { data, loading, error, refetch } = useCandidateWithApplicationsQuery({
    variables: { id: candidateId },
    skip: isNaN(candidateId),
  });

  const { data: scoreData } = useScorecardsQuery({
    variables: {
      first: 4,
      orderBy: ScorecardsOrderBy.IdAsc,
    },
  });

  const scorecards =
    scoreData?.scorecards?.nodes.map((scorecard) => ({
      id: (scorecard?.id || 0).toString(),
      title: scorecard?.translations?.nodes[0]?.title || "",
      point: 0,
      comment: "",
    })) || [];

  const candidate = data?.candidate;

  const candidateEvaluation =
    candidate?.evaluations.nodes.map((evaluation) => ({
      id: (evaluation.scorecards.nodes[0]?.id || 0).toString(),
      title:
        evaluation?.scorecards?.nodes[0]?.translations?.nodes[0]?.title || "",
      point: evaluation?.point || 0,
      comment: evaluation.comment,
    })) || [];

  scorecards.forEach((s) => {
    const exists = candidateEvaluation.find((e) => e.id === s.id);

    if (!exists) {
      candidateEvaluation.push(s);
    }
  });

  const finalEvaluations = Object.entries(
    candidateEvaluation.reduce((o, evaluation) => {
      const id = evaluation.id;

      if (scorecards.find((s) => s.id === id)) {
        o[id] = o[id] || [];
        o[id].push(evaluation);
      }

      return o;
    }, {} as Record<string, (typeof candidateEvaluation)[0][]>)
  ).map(([id, evaluations]) => {
    return {
      id,
      title: evaluations[0].title,
      point: evaluations.reduce((f, e) => f + e.point, 0) / evaluations.length,
      comment: evaluations
        .map((e) => e.comment)
        .join("\n")
        .trim(),
    };
  });

  const jobId = candidate?.jobsApplications?.nodes?.[0]?.job?.id || 0;
  const criteriaId = candidate?.jobsApplications.nodes[0].evaluation || 0;

  const { data: jobData } = useJobWithStagesQuery({
    variables: { id: jobId },
    skip: !jobId,
  });

  const isHired = !!(
    jobData?.job?.pipeline?.stages?.nodes.some(
      (stage) => stage.type === PipelineStageType.Hire
    ) &&
    candidate?.jobsApplications?.nodes?.[0]?.pipelineStage?.type ===
      PipelineStageType.Hire
  );

  const candidatePhoto = candidate?.photoUrl || person;
  const candidateName = candidate?.translations?.nodes?.[0]?.name || "بدون نام";
  const candidateJobs =
    candidate?.jobsApplications?.nodes
      ?.map((app) => app.job?.translations?.nodes?.[0]?.title)
      .filter(Boolean)
      .join(", ") || "بدون شغل";
  const candidateResumeUrl = candidate?.resumeUrl || "";

  const handleItemClick = (evaluation: { id: string; title: string }) => {
    setSelectedEvaluationId(parseInt(evaluation.id, 10));
    setSelectedScorecardTitle(evaluation.title);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedEvaluationId(null);
  };

  const selectedEvaluation = finalEvaluations.find(
    (evaluation) => evaluation.id === selectedEvaluationId?.toString()
  );

  if (loading) {
    return <CandidateSkeleton />;
  }

  if (error) {
    return <p className="text-center text-red-500">خطا در دریافت اطلاعات</p>;
  }

  if (!candidate?.nodeId) {
    return <EmptyPage title={"اطلاعاتی جهت نمایش وجود ندارد"} subtitle={""} />;
  }

  return (
    <main className="h-svh">
      <NavBar name={candidateJobs} />
      <div className="sm:h-11/12 max-sm:w-screen max-w-lg mx-auto sm:border sm:mt-8 sm:rounded-lg sm:items-center sm:shadow-lg">
        <Questionnaires
          key={candidateId}
          name={candidateName}
          job={candidateJobs}
          photo={candidatePhoto}
          resumeUrl={candidateResumeUrl}
          id={candidateId}
        />
        <EvaluationListItems
          evaluations={finalEvaluations}
          onItemClick={handleItemClick}
        />
        <CandidateActions
          candidate={candidate}
          jobData={jobData}
          candidateId={candidateId}
          isHired={isHired}
        />
      </div>
      {isModalOpen && selectedEvaluation?.point ? (
        <EvaluationDetailsModal
          evaluation={selectedEvaluation}
          onClose={handleModalClose}
        />
      ) : (
        <EvaluateModal
          open={isModalOpen}
          setOpen={setModalOpen}
          candidateId={candidateId}
          jobId={jobId}
          criteriaId={criteriaId}
          scorecardTitle={selectedScorecardTitle}
          scorecardId={selectedEvaluationId || 0}
          onSubmit={(evaluationScorecards, scorecards, candidateId) => {
            console.log("Evaluation Submitted:", {
              evaluationScorecards,
              scorecards,
              candidateId,
            });
            refetch();
          }}
        />
      )}
    </main>
  );
}
